import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { useEffect } from "react";
import NextScript from "next/script";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the Jazz Fan Pass?",
    answer:
      "The Jazz Fan Pass is a monthly subscription that gives you access to at least three Jazz games and one Mammoth game each month!",
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer:
      "Navigate to the Manage Subscription menu on the right side of your screen. There you can update your payment method and expiration information.",
  },
  {
    question: "How do I renew my subscription?",
    answer:
      "Your subscription will automatically renew on the 1st of the month. No action is needed to keep your subscription active.",
  },
  {
    question: "Can I pause my subscription?",
    answer:
      "Yes. In the Manage Subscription tab, you can pause your subscription during the opt-out window (15th-26th of each month). You'll receive a text when this period opens.",
  },
  {
    question: "Can I cancel my subscription?",
    answer:
      "You may cancel in the Manage Subscription tab, but only during the opt-out window (15th-26th of each month).",
  },
  {
    question:
      "How do I claim my tickets? When does the opt-in open before each game?",
    answer:
      "You will receive a text when the window to check in opens. The check-in window starts 24 hours prior to the game and closes 4 hours before game start. You must confirm attendance within this window to receive tickets.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not check in?",
    answer:
      "Tickets are guaranteed for each game on the schedule. There are no penalties for missing a game, but you must check in to claim your ticket.",
  },
  {
    question: "Where are my tickets? ",
    answer:
      "Tickets will be sent 3 hours prior to game start and can be accessed in your SeatGeek account.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "You will receive a text notification 3 hours before game start confirming that tickets have been delivered to your SeatGeek account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Jazz Fan Pass tickets and guest tickets are non-transferable and not eligible for resale. Abuse of these guidelines can result in revocation of your Jazz Fan Pass.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets are delivered to your account, they cannot be changed. For
        assistance, contact{" "}
        <Link href="mailto:tickets@utahjazz.com">tickets@utahjazz.com</Link>
      </>
    ),
  },

  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        Yes! Navigate to{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "utahjazz_pass" },
          }}
        >
          My Group
        </Link>{" "}
        in the right navigation menu to generate your invitation link and share
        it with friends. Fans in a ticket group will be seated together.
      </>
    ),
  },

  {
    question: "Can I bring a friend to a game?",
    answer:
      "If available, guest tickets may be purchased at the time of check-in.",
  },

  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue refunds for games you cannot attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        Please contact{" "}
        <Link href="mailto:tickets@utahjazz.com">tickets@utahjazz.com</Link> for
        any refund or cancellation requests.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By purchasing you understand that you are agreeing to a monthly
    subscription. It will renew each month unless you cancel during the defined
    opt-out period. Tickets are non-transferable and not eligible for resale.
    You also agree to receive SMS alerts regarding ticket delivery and agree to
    the{" "}
    <Link href="https://www.attend.tech/terms-of-use" target="_blank">
      Terms of Use
    </Link>{" "}
    &{" "}
    <Link href="https://www.attend.tech/privacy-policy" target="_blank">
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = () => {
  const router = useRouterExt();
  const { TEAM_SCHEMA } = useTeamConfig();

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.fbq) {
          window?.fbq("track", "PageView");
        }
        if (window?.gtag) {
          window?.gtag("event", "page_view", {
            page_location: url,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-google-tag-manager`}
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-MSC3T3L');
              console.log('======utahjazz google tag manager loaded=====');
          `,
        }}
      />

      <noscript id={`${TEAM_SCHEMA}-google-tag-manager-noscript`}>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-MSC3T3L"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
               !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '24832397563065286');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                const { cartRes } = e.detail;
                fbq('track', 'Purchase', {
                  value: cartRes.totalAmount,
                  currency: 'USD',
                });
                console.log('meta pixel purchase event fired.');
              });
            `,
        }}
      />
      <noscript>
        <Image
          height="1"
          width="1"
          alt="fb-pixel"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=24832397563065286&ev=PageView&noscript=1"
        />
      </noscript>

      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=AW-11346097754"
      />

      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'AW-11346097754');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              const { cartRes, checkoutRes } = e.detail;
              gtag('event', 'conversion', {
                'send_to': 'AW-11346097754/GCF5CKSpmJsbENrsnqIq',
                'value': cartRes.totalAmount,
                'currency': 'USD',
                'transaction_id': checkoutRes.orderNumber
              });
              console.log('gtag analytics purchase event fired.');
            });
        `,
        }}
      />
    </Head>
  );
};

export const REGISTRATION_TERMS_AND_CONDITIONS = () => (
  <>
    By checking this box you agree to receive notifications and offers from the
    Utah Jazz & Utah Mammoth and agree to the{" "}
    <Link href="https://www.attend.tech/terms-of-use" target="_blank">
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link href="https://www.attend.tech/privacy-policy" target="_blank">
      Privacy Policy
    </Link>
    .
  </>
);

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => <>You&apos;re all set to experience the action!</>,
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Invite your friends! Create groups{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                here
              </span>
            </u>
          </b>
          .
        </>
      );
    },
  };
